import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchVehicles = createAsyncThunk("vehicles/fetchVehicles", async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
  try {
    const r = await api.get("/Vehicules", { params: { page, pageSize } });
    return r.data; // PagedResult
  } catch (error) { return rejectWithValue(error.message || "Erreur de connexion"); }
});

export const createVehicle = createAsyncThunk("vehicles/createVehicle", async (vehicleData, { rejectWithValue }) => {
  try {
    const payload = {
      matricule: vehicleData.matricule || '',
      marque: vehicleData.marque,
      modele: vehicleData.modele,
      couleur: vehicleData.couleur,
      kilometrage: vehicleData.kilometrage,
      numeroCarteGrise: vehicleData.numeroCarteGrise,
      prixParJour: vehicleData.prixParJour,
      statut: vehicleData.statut || 0,
      typeCarburant: vehicleData.typeCarburant || 0,
      transmission: vehicleData.transmission || 0,
      imageUrl: vehicleData.imageUrl || '',
    };
    const created = await api.post("/Vehicules", payload);
    const list = await api.get('/Vehicules', { params: { page: 1, pageSize: 10 } });
    return { ...list.data, id: created.data }; // PagedResult + id du véhicule créé
  } catch (error) { return rejectWithValue(error.message || "Erreur création véhicule"); }
});

export const updateVehicle = createAsyncThunk("vehicles/updateVehicle", async ({ id, vehicleData }, { rejectWithValue }) => {
  try {
    const payload = {
      matricule: vehicleData.matricule || '',
      marque: vehicleData.marque,
      modele: vehicleData.modele,
      couleur: vehicleData.couleur,
      kilometrage: vehicleData.kilometrage,
      numeroCarteGrise: vehicleData.numeroCarteGrise,
      prixParJour: vehicleData.prixParJour,
      statut: vehicleData.statut || 0,
      typeCarburant: vehicleData.typeCarburant || 0,
      transmission: vehicleData.transmission || 0,
      imageUrl: vehicleData.imageUrl || '',
    };
    await api.patch(`/Vehicules/${id}`, payload);
    const list = await api.get('/Vehicules', { params: { page: 1, pageSize: 10 } });
    return list.data; // PagedResult
  } catch (error) { return rejectWithValue(error.message || "Erreur modification véhicule"); }
});

export const deleteVehicle = createAsyncThunk("vehicles/deleteVehicle", async (id, { rejectWithValue }) => {
  try { await api.delete(`/Vehicules/${id}`); return id; }
  catch (error) {
    const code = error.response?.data?.code;
    return rejectWithValue(code || error.message || "Erreur suppression véhicule");
  }
});

export const checkVehicleLinks = createAsyncThunk("vehicles/checkVehicleLinks", async (id, { rejectWithValue }) => {
  try { const r = await api.get(`/Vehicules/${id}/check-links`); return r.data; }
  catch (error) { return rejectWithValue(error.message); }
});

export const archiveVehicle = createAsyncThunk("vehicles/archiveVehicle", async (id, { rejectWithValue }) => {
  try { await api.patch(`/Vehicules/${id}/archive`); return id; }
  catch (error) { return rejectWithValue(error.message || "Erreur archivage véhicule"); }
});

export const fetchArchivedVehicles = createAsyncThunk("vehicles/fetchArchivedVehicles", async (_, { rejectWithValue }) => {
  try { const r = await api.get("/Vehicules/archived"); return r.data; }
  catch (error) { return rejectWithValue(error.message || "Erreur chargement véhicules archivés"); }
});

export const getByStatut = createAsyncThunk("vehicles/getByStatut", async (statut, { rejectWithValue }) => {
  try { const r = await api.get(`/Vehicules/statut/${statut}`); return r.data; }
  catch (error) { return rejectWithValue(error.message); }
});

export const getByMarque = createAsyncThunk("vehicles/getByMarque", async (marque, { rejectWithValue }) => {
  try { const r = await api.get(`/Vehicules/marque/${encodeURIComponent(marque)}`); return r.data; }
  catch (error) { return rejectWithValue(error.message); }
});

export const getStatistics = createAsyncThunk("vehicles/getStatistics", async (_, { rejectWithValue }) => {
  try { const r = await api.get('/Vehicules/statistiquesvehicule'); return r.data; }
  catch (error) { return rejectWithValue(error.message); }
});

const pagedFulfilled = (state, action) => {
  state.loading = false;
  state.items = action.payload.items;
  state.totalCount = action.payload.totalCount;
  state.totalPages = action.payload.totalPages;
  state.currentPage = action.payload.page ?? 1;
  state.success = true;
};

const initialState = {
  items: [],
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
  pageSize: 10,
  archivedItems: [],
  loading: false,
  error: null,
  success: false,
  statistics: { total: 0, disponible: 0, loue: 0, enMaintenance: 0, reservee: 0 },
};

const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    resetSuccess: (state) => { state.success = false; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchVehicles.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(createVehicle.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createVehicle.fulfilled, (state, action) => { pagedFulfilled(state, action); state.currentPage = 1; })
      .addCase(createVehicle.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(updateVehicle.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateVehicle.fulfilled, (state, action) => { pagedFulfilled(state, action); state.currentPage = 1; })
      .addCase(updateVehicle.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(deleteVehicle.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(v => v.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
      })
      .addCase(deleteVehicle.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(getByStatut.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(getByMarque.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(getStatistics.fulfilled, (state, action) => { state.loading = false; state.statistics = action.payload; });
    builder
      .addCase(archiveVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(v => v.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
      })
      .addCase(archiveVehicle.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(fetchArchivedVehicles.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchArchivedVehicles.fulfilled, (state, action) => { state.loading = false; state.archivedItems = action.payload; })
      .addCase(fetchArchivedVehicles.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { resetSuccess, clearError } = vehiclesSlice.actions;
export default vehiclesSlice.reducer;
